import { useLocation, Link, Outlet } from 'react-router-dom';
import {
  Sidebar,
  SidebarProvider,
  SidebarInset,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarTrigger,
} from '@/components/design-system/sidebar';
import type { NavigationItemType } from '@/types/nav.types';

function normalizePath(path: string): string {
  // Remove trailing slash, except for root path
  return path === '/' ? path : path.replace(/\/$/, '');
}

export function SidebarAppLayout({
  navigationItems,
  appName = 'My App',
}: {
  navigationItems: NavigationItemType[];
  appName?: string;
}) {
  const location = useLocation();

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="flex h-screen w-full">
        <Sidebar>
          <SidebarHeader className="border-b px-4 py-4">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold">{appName}</h1>
            </div>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup className="pt-4">
              <SidebarGroupContent>
                <SidebarMenu>
                  {[
                    ...new Map(
                      navigationItems.map(item => [item.title, item])
                    ).values(),
                  ]
                    .filter(item => !item.isInternal)
                    .map(item => {
                      const isActive =
                        normalizePath(location.pathname) ===
                        normalizePath(item.path);
                      return (
                        <SidebarMenuItem key={item.path}>
                          <SidebarMenuButton asChild isActive={isActive}>
                            <Link
                              to={item.path}
                              className="flex items-center gap-3"
                            >
                              <item.icon className="h-4 w-4 shrink-0" />
                              <span className="truncate capitalize">
                                {item.title}
                              </span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>

        <SidebarInset className="flex-1 relative">
          <main className="flex-1 overflow-auto h-full">
            <Outlet />
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
